/* ================================================================
   Pack3D Designer - Core Data Model Types
   Maps 1:1 to legacy crate/pallet model semantics.
   ================================================================ */

// --------------- Primitives ---------------

export interface Dimensions3 {
  length: number;
  width: number;
  height: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// --------------- Project-level ---------------

export type ProjectMode = 'crate' | 'pallet';

export interface ProjectMeta {
  id: string;
  name: string;
  customerName: string;
  mode: ProjectMode;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSymmetry {
  side: boolean;   // SideSymmetry
  end: boolean;    // EndSymmetry
}

// --------------- Specification / Conditions (new) ---------------

/**
 * 规范：结构设计所引用的行业或内部标准。
 * 对应 tool2 "designCrateForm_1 一、规范" 面板。
 */
export interface SpecificationProfile {
  /** Code 例如 "ISPM-15", "GB/T 4857", "内部-A", "自定义" */
  code: string;
  /** 展示名称 */
  name: string;
  /** 规范类型 */
  category: 'heat-treatment' | 'fumigation' | 'plywood' | 'standard-wood' | 'custom';
  /** 最小板厚 mm */
  minBoardThickness?: number;
  /** 最小框架厚 mm */
  minFrameThickness?: number;
  /** 推荐堆码层数 */
  recommendedStackLayers?: number;
}

/**
 * 条件输入：环境、堆码、运输方式。
 * 对应 tool2 "designCrateForm_3 二、条件输入"。
 */
export interface ProjectConditions {
  /** 堆码层数 */
  stackLayers: number;
  /** 环境：indoor / outdoor / export */
  environment: 'indoor' | 'outdoor' | 'export';
  /** 运输方式 */
  transportMode: 'road' | 'sea' | 'air' | 'rail' | 'mixed';
  /** 湿度区间 */
  humidity: 'dry' | 'normal' | 'humid';
  /** 是否需要防静电 */
  antiStatic: boolean;
  /** 是否需要防潮 */
  moistureBarrier: boolean;
}

/** 商品净尺寸与留空 */
export interface GoodsClearance {
  /** 前后留空 mm */
  lengthClearance: number;
  /** 左右留空 mm */
  widthClearance: number;
  /** 上下留空 mm */
  heightClearance: number;
}

// --------------- Material Library (new) ---------------

/**
 * 富材质：原有的 `materialKey: number` 只是外键，实际参数在这里。
 * 为了渐进迁移，runtime 里 `materialKey` 仍然是 number，但解析时
 * 查 `project.materialLibrary[key]` 得到详细参数。
 */
export interface Material {
  key: number;
  name: string;
  /** 基础色（未被 layer.color 覆盖时使用） */
  color: string;
  /** 密度 kg/m³（用于重量/成本） */
  densityKgPerM3: number;
  /** 单价，元/m³ */
  pricePerM3: number;
  /** 材质类别 */
  kind: 'plywood' | 'softwood' | 'hardwood' | 'osb' | 'lvl' | 'metal' | 'composite';
  /** 抗弯模量 MPa，用于校验 */
  bendingStrengthMPa?: number;
  /** 贴图 URL（相对于 public/，例如 "/textures/material/maweisong.jpg"） */
  textureUrl?: string;
  /** 纹理循环尺寸 mm（一个 tile 代表的真实长度，默认 200） */
  textureTileMm?: number;
  /** 备注 */
  remark?: string;
}

// --------------- Template (new) ---------------

/**
 * 结构模板：一键应用整个 project 的参数。
 * 对应 tool2 "新建从模板创建"。
 */
export interface ProjectTemplate {
  id: string;
  name: string;
  category: 'crate' | 'pallet' | 'steelband' | 'custom';
  description: string;
  /** 建议适用场景 */
  scenarioTags: string[];
  /**
   * 应用时会作为新 project 的起点。
   * 字段与 ProjectModel 兼容（但 meta 会重新生成）。
   */
  payload: Partial<ProjectModel>;
}

export interface SideSpanOverride {
  spanU: number;
  spanV: number;
  offsetU: number;
  offsetV: number;
}

export interface ProjectModel {
  meta: ProjectMeta;
  innerSize: Dimensions3;
  outerSize: Dimensions3;
  goodsSize: Dimensions3;
  payloadKg: number;
  grossWeightKg: number;
  symmetry: ProjectSymmetry;
  selfAdaption: boolean;
  /** 前后板 / 左右端板是否跟随相邻面厚度联动 */
  sideLinkageMode?: 'linked' | 'independent';
  /** 联动关闭时冻结的侧面尺寸/位置基准 */
  sideSpanOverrides?: Partial<Record<SideId, SideSpanOverride>>;
  sides: Side[];

  // ---- Extended domain (optional for back-compat) ----
  specification?: SpecificationProfile;
  conditions?: ProjectConditions;
  clearance?: GoodsClearance;
  /** Project-local material catalog snapshot. */
  materialLibrary?: Material[];

  /** Opaque legacy fields for round-trip compat */
  _legacy?: Record<string, unknown>;
}

// --------------- Side-level ---------------

/** Canonical side identifiers (mapped from legacy 0-5 indices) */
export type SideId =
  | 'pedestal'       // 0 - bottom / base
  | 'sideFront'      // 1 - side face qian
  | 'endLeft'        // 2 - end face left
  | 'endRight'       // 3 - end face right
  | 'sideBack'       // 4 - side face hou
  | 'topCap';        // 5 - top cap

export const SIDE_ID_LIST: SideId[] = [
  'pedestal', 'sideFront', 'endLeft', 'endRight', 'sideBack', 'topCap',
];

/** Map legacy numeric index to SideId */
export const LEGACY_SIDE_INDEX: Record<number, SideId> = {
  0: 'pedestal',
  1: 'sideFront',
  2: 'endLeft',
  3: 'endRight',
  4: 'sideBack',
  5: 'topCap',
};

/** Map legacy string names to SideId */
export const LEGACY_SIDE_NAME: Record<string, SideId> = {
  'Pedestal': 'pedestal',
  'SideFaceQian': 'sideFront',
  'EndFaceLeft': 'endLeft',
  'EndFaceRight': 'endRight',
  'SideFaceHou': 'sideBack',
  'TopCap': 'topCap',
};

export const SIDE_PAIR: Record<SideId, SideId | null> = {
  pedestal: 'topCap',
  sideFront: 'sideBack',
  endLeft: 'endRight',
  endRight: 'endLeft',
  sideBack: 'sideFront',
  topCap: 'pedestal',
};

export interface Side {
  sideId: SideId;
  pairSideId: SideId | null;
  enabled: boolean;
  layers: Layer[];
  /** Legacy adjustParam for round-trip compat */
  _legacyAdjustParam?: Record<string, unknown>;
}

// --------------- Layer-level ---------------

/**
 * Style types (maps to legacy styleSetting.style):
 *  0 = board (板条)
 *  1 = singleBoard (整板)
 *  3 = crossGrid (田字: 框架 + 横撑组成网格)
 *  4 = timber block (木方/垫块)
 */
export type LayerStyle = 0 | 1 | 3 | 4;

export interface PositionLogic {
  bottom: number;
  toppos: number;
  sidepos: number;
  endpos: number;
}

export interface SizeLogic {
  frame: number;
  horBrace: number;
  board: number;
  bevelBrace: number;
}

export interface SpaceLogic {
  frame: number;
  horBrace: number;
  board: number;
}

export interface CountLogic {
  horBrace: number;
  bevelBrace: number;
}

export interface StyleLogics {
  position: PositionLogic;
  size: SizeLogic;
  space: SpaceLogic;
  count: CountLogic;
}

export interface Directions {
  mountain: number;
  frame: number;
  isHor: 0 | 1;
  horBrace: number;
  toward: number[];
}

export interface CornerPackConfig {
  corner: boolean;
  pack: { num: number; space: number };
}

export interface Counts {
  frame: number[];
  board: number;
  horBrace: number[];
  bevelBrace: number[];
}

export interface StyleSetting {
  style: LayerStyle;
  material: number;
  materialcolor: string;
  CornerPack: CornerPackConfig;
  logics: StyleLogics;
  directions: Directions;
  count: Counts;
}

export interface BoardElement {
  width: number;
  thickness: number;
  length: number;
}

export interface OpeningConfig {
  depth: number;
  margins: number;
  radius: number;
  span: number;
  space: number;
}

/**
 * Style-4 (厚板条) explicit board dimensions.
 * Replaces the old "block grid" semantics — these are thick planks,
 * not short cubic blocks.
 */
export interface BlockBoardConfig {
  /** Board length in mm (0 = auto, span full along direction) */
  boardLength: number;
  /** Board width in mm (cross-section width of each plank) */
  boardWidth: number;
  /** Number of planks distributed evenly */
  count: number;
  /** Legacy field kept only for round-trip compatibility; solver ignores it */
  centerSpacing?: number;
}

export interface SizeGroups {
  board: BoardElement[];
  frame: BoardElement[][];
  horBrace: BoardElement[][];
}

/**
 * 单根构件尺寸覆盖（板条/木方）。
 * solver 先按统一参数生成，再用 override 替换指定构件的尺寸。
 */
export interface ElementOverride {
  /** 构件索引 (0-based，对应 solver 生成顺序) */
  index: number;
  length?: number;
  width?: number;
  thickness?: number;
}

export interface Layer {
  id: string;
  name: string;
  style: LayerStyle;
  profile: string;            // descriptive label
  direction: 0 | 1;
  thickness: number;
  materialKey: number;
  color: string;
  offset: Vec3;
  mirrored: boolean;
  /** Full style setting - direct legacy compat */
  styleSetting: StyleSetting;
  /** Resolved size arrays per component type */
  sizeGroups: SizeGroups;
  /** Opening / slot config (legacy kaicao) */
  openings: OpeningConfig;
  /** Style-4 thick-board config (replaces block grid) */
  blockBoard: BlockBoardConfig;
  /** Is this the base layer for the side */
  isBase: boolean;
  /** Legacy m_recordsno = sideNo*10 + layerIndex */
  recordsNo: number;
  /** Legacy layer index within side */
  layerIndex: number;
  /** 单根构件尺寸覆盖，按 index 匹配 solver 输出的板条 */
  elementOverrides?: ElementOverride[];
  /** Opaque legacy fields */
  _legacy?: Record<string, unknown>;
}

// --------------- Draw Records (intermediate) ---------------

export interface PositionedElement {
  width: number;
  thickness: number;
  length: number;
  position: Vec3;
  material: number;
  materialcolor: string;
  meshId: string;   // "sideIdx-layerIdx-elemIdx"
}

export interface DrawRecord {
  recordNo: number;
  sideId: SideId;
  sideIndex: number;
  layerIndex: number;
  boards: PositionedElement[];
  frames: PositionedElement[][];
  horBraces: PositionedElement[][];
  position: Vec3;
}

// --------------- Render Nodes ---------------

export interface RenderNode {
  meshId: string;
  geometry: {
    width: number;
    height: number;
    depth: number;
  };
  position: Vec3;
  material: number;
  materialcolor: string;
  sideId: SideId;
  sideIndex: number;
  layerIndex: number;
  elementIndex: number;
  elementType: 'board' | 'frame' | 'horBrace' | 'bevelBrace';
}

// --------------- Selection ---------------

export interface SelectionTarget {
  meshId: string;
  sideId: SideId;
  layerIndex: number;
  elementIndex: number;
  elementType: string;
}

// --------------- BOM (derived) ---------------

/**
 * BOM 行项：一种材质 + 规格的汇总。
 * 由 core/bom 从 solvedLayouts + materialLibrary 推导。
 */
export interface BomLine {
  materialKey: number;
  materialName: string;
  /** 规格标签例如 "18×90×1200" */
  spec: string;
  count: number;
  /** 单件面积 m²（最小边作厚度，另两边相乘） */
  unitAreaM2: number;
  /** 单件体积 m³ */
  unitVolumeM3: number;
  /** 总面积 m² */
  totalAreaM2: number;
  /** 总体积 m³ */
  totalVolumeM3: number;
  /** 总重量 kg */
  totalWeightKg: number;
  /** 总成本 ¥ */
  totalCostYuan: number;
  /** 出自哪些面 */
  fromSides: SideId[];
}

export interface BomSummary {
  lines: BomLine[];
  totalVolumeM3: number;
  totalWeightKg: number;
  totalCostYuan: number;
  meshCount: number;
}

// --------------- Validator (derived) ---------------

export type ValidationSeverity = 'info' | 'warning' | 'error';

export interface ValidationIssue {
  id: string;
  severity: ValidationSeverity;
  scope: 'project' | 'side' | 'layer';
  sideId?: SideId;
  layerIndex?: number;
  title: string;
  detail: string;
  /** 建议动作 */
  hint?: string;
}

export interface ValidationReport {
  issues: ValidationIssue[];
  errorCount: number;
  warningCount: number;
  infoCount: number;
  passed: boolean;
}

// --------------- Editor snapshot for undo ---------------

export interface EditorSnapshot {
  project: ProjectModel;
  timestamp: number;
}
